import { useCheckout } from '@stripe/react-stripe-js/checkout';
import { Environment } from '@util/Environment';
import { App } from 'hydra-data-types';
import React, { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Color } from '../../config/Color';
import { ActionType } from '../../store/ActionTypes';
import { useAppDispatch, useAppStore } from '../../store/Store';
import { PurchaseSelector } from '../../store/purchase/PurchaseActions';
import { useIsPaymentRequiredForCheckout } from '../../util/Payments';
import { ContentLoading } from '../styles/ContentLoading';
import { Spacer } from '../styles/Dividers';
import { BaseErrorMessage } from '../styles/ErrorMessage';
import { NextButton } from '../styles/LayoutButtons';
import { TickList } from '../styles/List';
import { Heading4 } from '../styles/Typography';
import { CreditCardWhyNowButton, CreditCardWhyNowSection } from './CreditCardWhyNow';
import { Disclaimer } from './Disclaimer';
import { StoreProof } from './StoreProof';
import { StripePaymentElement } from './StripePaymentElement';

export type CollectCardFormProps = {
  nextPage: (isNewUser?: boolean) => void;
  /** Replaces the disclaimer's container, for a page with its own look. */
  disclaimer?: ReactNode;
};

export const CollectCardForm: FunctionComponent<CollectCardFormProps> = (props) => {
  const formRef = useRef<null | HTMLFormElement>(null);
  const checkout = useCheckout();
  const dispatch = useAppDispatch();

  const { purchase, hasTrial, hasFreeTrial } = useAppStore((state) => ({
    purchase: state.purchase,
    hasTrial: PurchaseSelector.hasTrial(state.purchase),
    hasFreeTrial: PurchaseSelector.hasFreeTrial(state.purchase),
  }));

  const [isWhyNowOpen, setIsWhyNowOpen] = useState(false);

  const loading = purchase.intent.loading || purchase.payment.loading;

  const isPaymentRequired = useIsPaymentRequiredForCheckout(checkout);

  const getButtonText = () => {
    if (hasTrial) return `Start ${hasFreeTrial ? 'free ' : ''}trial`;
    return 'Start now';
  };

  useEffect(
    function handlePaymentSuccess() {
      if (purchase.checkoutId !== undefined) {
        props.nextPage();
      }
    },
    [purchase.checkoutId, props.nextPage]
  );

  useEffect(
    function handlePaymentError() {
      if (purchase.payment.error) {
        formRef.current?.scrollIntoView();
      }
    },
    [purchase.payment.error]
  );

  function handleCardPurchase() {
    if (checkout.type !== 'success') {
      return;
    }

    dispatch({
      type: ActionType.CONFIRM_CHECKOUT,
      checkout: checkout.checkout,
    });
  }

  // User tried to submit form without clicking on button
  function preventDefaultFormSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
  }

  if (purchase.intent.loading || checkout.type === 'loading') {
    return <ContentLoading />;
  }

  return (
    <form data-test="collectCardForm" onSubmit={preventDefaultFormSubmit} ref={formRef}>
      {isPaymentRequired && (
        <>
          <BillingDetailsHeader>
            <Heading4 style={{ textAlign: 'left', flex: 1 }}>Billing Details</Heading4>
            {hasFreeTrial && (
              <CreditCardWhyNowButton isOpen={isWhyNowOpen} setIsOpen={setIsWhyNowOpen} />
            )}
          </BillingDetailsHeader>

          <CreditCardWhyNowSection isOpen={isWhyNowOpen} />

          <PaymentSecurity>
            {hasTrial && <>If you cancel any time before your trial ends you won’t be charged. </>}
            All billing details are secure and encrypted.
          </PaymentSecurity>

          <StripePaymentElement />
        </>
      )}

      {purchase.payment.error && (
        <BaseErrorMessage data-test="cardError">{purchase.payment.error.message}</BaseErrorMessage>
      )}

      {Environment.APP === App.Nerva && (
        <>
          <Spacer space={20} mobile={20} />
          <StoreProof />
        </>
      )}

      <Spacer space={20} mobile={20} />
      {isPaymentRequired && (props.disclaimer ?? <Disclaimer />)}
      <NextButton hideArrow onClick={handleCardPurchase} loading={loading} data-test="submitButton">
        {getButtonText()}
      </NextButton>
      {hasTrial && (
        <PaymentFeatures>
          <li>No commitment &mdash; cancel any time</li>
        </PaymentFeatures>
      )}
    </form>
  );
};

const BillingDetailsHeader = styled.div`
  display: flex;
`;

const PaymentSecurity = styled.div`
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize.xxs};
  margin-bottom: 1.5em;
  color: ${Color.content.default};
`;

const PaymentFeatures = styled(TickList)`
  margin: 1.75em 2em;
`;
